/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MaskedInput from "antd-mask-input";
import {
  Form,
  Input,
  Button,
  Checkbox,
  DatePicker,
  Select,
  Divider,
  Space,
  Tooltip,
  Row,
  Col,
} from "antd";
import { MinusCircleOutlined, PlusCircleFilled } from "@ant-design/icons";
import { RootStateOrAny, useSelector } from "react-redux";
import { PhoneType } from "../../lib/interfaces/PhoneType";
import usePhoneMask from "../../lib/hooks/usePhoneMask";
import "./AccountCreate.less";
import { hasLocalAccessNode } from "../../lib/configs";
import { getSelectOptions } from "../../lib/utils/getSelectOptions";
import { useAppSelector, useAppDispatch } from "../..";
import { timezones } from "../../lib/const/timezones";
import { selectFilterOption } from "../../lib/utils/selectFilterOption";
import { ResponsibleUserFormItem } from "./ResponsibleUser/ResponsibleUserFormItem";
import { ContactIndividualFormItem } from "./ContactIndividual/ContactIndividualFormItem";
import { fetchAllUsers } from "../../store/slices/accountsTs";
import { AccountCreateActiveModal } from "./types/AccountCreateActiveModal";
import { CreateAccountData } from "./types/CreateAccountData";
import { useActiveModal } from "../../lib/utils";
import { baseUrlName } from "../../lib/routes/baseUrl";

const { Option } = Select;

const formItemLayout = {
  labelCol: {
    span: 4,
  },
  wrapperCol: {
    span: 8,
  },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    span: 8,
    offset: 4,
  },
};

const validationLength = {
  INN: 10,
  BIN: 12,
  KPP: 9,
};

interface IAccountCreate {
  createOrganizationAccount: (props: { params: any; callback: any }) => void;
  isCreateInProcess: boolean;
  neededProperties: string[];
}

function AccountCreate(props: IAccountCreate) {
  const { createOrganizationAccount, neededProperties, isCreateInProcess } = props;
  const dispatch = useAppDispatch();

  const {
    activeModal,
    setActiveModal,
    activeModalOptions,
  } = useActiveModal<AccountCreateActiveModal>("");

  // Загрузка всех пользователей для выбора менеджера
  useEffect(() => {
    if (activeModal === "responsibleSelect") {
      dispatch(fetchAllUsers({}));
    }
  }, [activeModal]);

  const { accountStatuses, countries, regions, accountTypes } = useAppSelector(
    (state) => state.dicts
  );

  const { organizationPropertyTypes } = useAppSelector((state) => state.propertyTypesAdmin);

  const phoneTypes: PhoneType[] = useSelector((state: RootStateOrAny) => state.dicts.phoneTypes);

  const { phoneMask, cleanStringNumber } = usePhoneMask();

  const [form] = Form.useForm<CreateAccountData>();
  const [isSame, setIsSame] = useState(true);

  const createdProperties = neededProperties.reduce((result, typeId) => {
    result[typeId] = undefined;
    return result;
  }, {});

  const initialValues: CreateAccountData = {
    createDate: undefined,
    status: undefined,
    accTypeId: 1,
    exiteId: [""],
    fullName: undefined,
    shortName: undefined,
    ...createdProperties,
    email: [""],
    phones: [{ countryCode: "", phone: "", phoneTypeId: 1 }],
    country: undefined,
    index: undefined,
    region: undefined,
    district: undefined,
    city: undefined,
    village: undefined,
    street: undefined,
    house: undefined,
    building: undefined,
    apartment: undefined,
    unstructuredAddress: undefined,
    isSame: true,
    actualCountry: undefined,
    actualIndex: undefined,
    actualRegion: undefined,
    actualDistrict: undefined,
    actualCity: undefined,
    actualVillage: undefined,
    actualStreet: undefined,
    actualHouse: undefined,
    actualBuilding: undefined,
    actualApartment: undefined,
    actualUnstructuredAddress: undefined,
  };

  const onFinish = (params: CreateAccountData) => {
    // return console.log(params);
    const phones =
      params.phones
        .map((phoneItem) => ({
          ...phoneItem,
          phone: cleanStringNumber(phoneItem.phone),
          countryCode: cleanStringNumber(phoneItem.countryCode),
        }))
        .filter((phoneItem) => !!phoneItem.phone) || [];

    createOrganizationAccount({ params: { ...params, phones }, callback: form.resetFields });
  };

  return (
    <div className="account-create">
      <h1>Создание аккаунта юридического лица</h1>
      <Divider />
      <Form
        form={form}
        labelCol={formItemLayout.labelCol}
        wrapperCol={formItemLayout.wrapperCol}
        labelAlign="left"
        initialValues={initialValues}
        onFinish={onFinish}
      >
        <Form.Item
          label="Дата создания"
          name="createDate"
          rules={[
            {
              required: true,
            },
          ]}
          className="form-item-required__hidden-error"
        >
          <DatePicker />
        </Form.Item>
        <Divider />
        {hasLocalAccessNode(
          "account.create.accountType",
          <>
            <h3>Тип оператора</h3>
            <Form.Item
              label="Тип оператора"
              name="accountType"
              rules={[
                {
                  required: true,
                },
              ]}
              className="form-item-required__hidden-error"
            >
              <Select
                options={getSelectOptions({
                  list: accountTypes,
                })}
              />
            </Form.Item>
            <Divider />
          </>
        )}
        <h3>Статус</h3>
        <Form.Item
          label="Cтатус"
          name="status"
          rules={[
            {
              required: true,
            },
          ]}
          className="form-item-required__hidden-error"
        >
          <Select>
            {accountStatuses.map((status) => (
              <Option key={status.id} value={status.id}>
                {status.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <h3>Тип аккаунта</h3>
        <Form.Item
          label="Тип аккаунта"
          name="accTypeId"
          rules={[
            {
              required: true,
            },
          ]}
          className="form-item-required__hidden-error"
        >
          <Select>
            {accountTypes.map((accType) => (
              <Option key={accType.id} value={accType.id}>
                {accType.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Divider />
        {hasLocalAccessNode(
          "account.create.exiteId",
          <>
            <Form.List name="exiteId">
              {(fields, { add, remove }, { errors }) => (
                <>
                  <Space className="list-title">
                    <h3 className="list-title__element">Технический ID</h3>
                    <Form.Item className="list-title__element">
                      <Tooltip title="Добавить Технический ID">
                        <PlusCircleFilled className="plus-icon" onClick={() => add()} />
                      </Tooltip>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  </Space>
                  {fields.map((field, index) => (
                    <Form.Item
                      {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                      label={index === 0 ? "Технический ID" : ""}
                      required={false}
                      key={field.key}
                    >
                      <Form.Item
                        {...field}
                        validateTrigger={["onChange", "onBlur"]}
                        rules={[
                          {
                            required: false,
                            whitespace: true,
                          },
                        ]}
                        noStyle
                      >
                        <Input className="input-with-icon" />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <MinusCircleOutlined
                          className="dynamic-delete-button minus-icon"
                          onClick={() => remove(field.name)}
                        />
                      ) : null}
                    </Form.Item>
                  ))}
                </>
              )}
            </Form.List>
            <Divider />
          </>
        )}
        <h3>Наименование организации</h3>
        <Form.Item
          label="Полное наименование"
          name="fullName"
          rules={[{ required: true }]}
          className="form-item-required__hidden-error"
        >
          <Input />
        </Form.Item>
        {hasLocalAccessNode(
          "account.create.shortName",
          <Form.Item
            label="Краткое наименование"
            name="shortName"
            rules={[{ required: true }]}
            className="form-item-required__hidden-error"
          >
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.code",
          <Form.Item
            label="Код"
            name="code"
            rules={[{ required: true }]}
            className="form-item-required__hidden-error"
          >
            <Input />
          </Form.Item>
        )}
        <Divider />
        {hasLocalAccessNode(
          "account.create.requisites",
          <>
            <h3>Реквизиты</h3>
            {organizationPropertyTypes
              .filter((item) => neededProperties.includes(String(item.alias)))
              .map((prop) => {
                const regex = `^\\d{${validationLength[prop.alias]}}$`;
                const placeholder = validationLength[prop.alias]
                  ? `Введите ${validationLength[prop.alias]} символов`
                  : "";
                return (
                  <Form.Item
                    key={prop.id}
                    label={prop.name}
                    name={prop.alias}
                    rules={
                      validationLength[prop.alias]
                        ? [
                            {
                              required: true,
                              len: validationLength[prop.alias],
                              pattern: new RegExp(regex),
                            },
                          ]
                        : undefined
                    }
                    className="form-item-required__hidden-error"
                  >
                    <Input placeholder={placeholder} />
                  </Form.Item>
                );
              })}
            <Divider />
          </>
        )}
        <Form.List name="phones">
          {(fields, { add, remove }) => {
            const getTitle = (title, index) =>
              index === 0 ? (
                <div
                  style={{
                    fontSize: "13px",
                    margin: "3px 0 5px 0",
                    color: "rgba(0, 0, 0, 0.85)",
                  }}
                >
                  {title}
                </div>
              ) : null;
            return (
              <Row style={{ display: "flex", alignItems: "top" }}>
                <Col span={4} style={{ display: "flex" }}>
                  <h3>Контактный телефон</h3>
                  <Tooltip title="Добавить контактный телефон">
                    <PlusCircleFilled
                      className="plus-icon"
                      style={{ margin: "6px 0 0 8px" }}
                      onClick={() => add({ phoneTypeId: 1 })}
                    />
                  </Tooltip>
                </Col>
                <Col span={20}>
                  {fields.map(({ name, key, ...field }, index) => {
                    return (
                      <Form.Item key={key} wrapperCol={{ span: 24 }}>
                        <Row>
                          <Col span={2}>
                            {getTitle("Код страны", index)}
                            <Form.Item
                              {...field}
                              name={[name, "countryCode"]}
                              className="form-item-required__hidden-error__mb0"
                            >
                              <MaskedInput mask={phoneMask.country} name="countryCode" allowClear />
                            </Form.Item>
                          </Col>
                          <Col span={5} style={{ marginLeft: "10px" }}>
                            {getTitle("Номер", index)}
                            <Form.Item
                              {...field}
                              name={[name, "phone"]}
                              className="form-item-required__hidden-error__mb0"
                            >
                              <MaskedInput mask={phoneMask.phone} name="phone" allowClear />
                            </Form.Item>
                          </Col>
                          <Col span={4} style={{ marginLeft: "10px" }}>
                            {getTitle("Тип номера", index)}
                            <Form.Item
                              {...field}
                              name={[name, "phoneTypeId"]}
                              className="form-item-required__hidden-error__mb0"
                            >
                              <Select>
                                {phoneTypes.map((phoneType) => (
                                  <Select.Option value={phoneType.id} key={phoneType.id}>
                                    {phoneType.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={1}>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                className="dynamic-delete-button minus-icon"
                                style={{
                                  marginTop: index === 0 ? "38px" : "10px",
                                  marginLeft: "5px",
                                }}
                                onClick={() => remove(name)}
                              />
                            )}
                          </Col>
                        </Row>
                      </Form.Item>
                    );
                  })}
                </Col>
              </Row>
            );
          }}
        </Form.List>
        <Divider />
        <Form.List name="email">
          {(fields, { add, remove }, { errors }) => (
            <>
              <Space className="list-title">
                <h3 className="list-title__element">Электронная почта</h3>
                <Form.Item className="list-title__element">
                  <Tooltip title="Добавить электронную почту">
                    <PlusCircleFilled className="plus-icon" onClick={() => add()} />
                  </Tooltip>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </Space>
              {fields.map((field, index) => (
                <Form.Item
                  {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                  label={index === 0 ? "Электронная почта" : ""}
                  key={field.key}
                  required
                >
                  <Form.Item
                    {...field}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        type: "email",
                        message: "Электронная почта введена некорректно",
                      },
                    ]}
                    noStyle
                  >
                    <Input className="input-with-icon" />
                  </Form.Item>
                  {fields.length > 1 ? (
                    <MinusCircleOutlined
                      className="dynamic-delete-button minus-icon"
                      onClick={() => remove(field.name)}
                    />
                  ) : null}
                </Form.Item>
              ))}
            </>
          )}
        </Form.List>
        <Divider />
        <h3>Юридический адрес</h3>
        <Form.Item
          label="Регион"
          name="region"
          rules={[
            {
              required: true,
            },
          ]}
          className="form-item-required__hidden-error"
        >
          <Select
            showSearch
            filterOption={selectFilterOption}
            options={getSelectOptions({
              list: regions,
            })}
          />
        </Form.Item>
        {hasLocalAccessNode(
          "account.create.index",
          <Form.Item
            label="Индекс"
            name="index"
            rules={[{ max: 10, message: "Максимум 10 символов" }]}
          >
            <Input />
          </Form.Item>
        )}

        <Form.Item
          label="Страна"
          name="country"
          rules={[{ required: true }]}
          className="form-item-required__hidden-error"
        >
          <Select
            options={getSelectOptions({
              list: countries,
            })}
            showSearch
            filterOption={selectFilterOption}
          />
        </Form.Item>
        {hasLocalAccessNode(
          "account.create.district",
          <Form.Item label="Район" name="district">
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.city",
          <Form.Item label="Город" name="city">
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.village",
          <Form.Item label="Населенный пункт" name="village">
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.street",
          <Form.Item label="Улица" name="street">
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.house",
          <Form.Item label="Дом" name="house">
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.building",
          <Form.Item label="Корпус" name="building">
            <Input />
          </Form.Item>
        )}
        {hasLocalAccessNode(
          "account.create.apartment",
          <Form.Item label="Офис" name="apartment">
            <Input />
          </Form.Item>
        )}
        <Form.Item label="Адрес" name="unstructuredAddress">
          <Input.TextArea placeholder="Введите адрес в свободной форме" />
        </Form.Item>
        <Divider />
        <h3>Почтовый адрес</h3>
        <Form.Item name="isSame" valuePropName="checked">
          <Checkbox onChange={() => setIsSame(form.getFieldValue("isSame"))}>
            Совпадает с юридическим адресом
          </Checkbox>
        </Form.Item>
        {isSame || (
          <>
            <Form.Item
              label="Регион"
              name="actualRegion"
              rules={[
                {
                  required: true,
                },
              ]}
              className="form-item-required__hidden-error"
            >
              <Select
                showSearch
                filterOption={selectFilterOption}
                options={getSelectOptions({
                  list: regions,
                })}
              />
            </Form.Item>
            {hasLocalAccessNode(
              "account.create.actualIndex",
              <Form.Item
                label="Индекс"
                name="actualIndex"
                rules={[{ max: 10, message: "Максимум 10 символов" }]}
              >
                <Input />
              </Form.Item>
            )}
            <Form.Item
              label="Страна"
              name="actualCountry"
              rules={[{ required: true }]}
              className="form-item-required__hidden-error"
            >
              <Select
                options={getSelectOptions({
                  list: countries,
                })}
                showSearch
                filterOption={selectFilterOption}
              />
            </Form.Item>
            {hasLocalAccessNode(
              "account.create.actualDistrict",
              <Form.Item label="Район" name="actualDistrict">
                <Input />
              </Form.Item>
            )}
            {hasLocalAccessNode(
              "account.create.actualCity",
              <Form.Item label="Город" name="actualCity">
                <Input />
              </Form.Item>
            )}
            {hasLocalAccessNode(
              "account.create.actualVillage",
              <Form.Item label="Населенный пункт" name="actualVillage">
                <Input />
              </Form.Item>
            )}
            {hasLocalAccessNode(
              "account.create.actualStreet",
              <Form.Item label="Улица" name="actualStreet">
                <Input />
              </Form.Item>
            )}
            {hasLocalAccessNode(
              "account.create.actualHouse",
              <Form.Item label="Дом" name="actualHouse">
                <Input />
              </Form.Item>
            )}
            {hasLocalAccessNode(
              "account.create.actualBuilding",
              <Form.Item label="Корпус" name="actualBuilding">
                <Input />
              </Form.Item>
            )}
            {hasLocalAccessNode(
              "account.create.actualApartment",
              <Form.Item label="Офис" name="actualApartment">
                <Input />
              </Form.Item>
            )}
            <Form.Item label="Адрес" name="actualUnstructuredAddress">
              <Input.TextArea placeholder="Введите адрес в свободной форме" />
            </Form.Item>
          </>
        )}
        <Divider />
        {hasLocalAccessNode(
          "account.create.timezone",
          <>
            <h3>Местное время оператора</h3>
            <Form.Item name="timezone" label="Часовой пояс">
              <Select
                allowClear
                options={getSelectOptions({
                  list: timezones,
                  labelName: "name",
                  valueName: "name",
                })}
              />
            </Form.Item>
            <Divider />
          </>
        )}
        {hasLocalAccessNode(
          "account.create.responsibleUsers",
          <ResponsibleUserFormItem
            activeModal={activeModal}
            setActiveModal={setActiveModal}
            formInstance={form}
          />
        )}
        {hasLocalAccessNode(
          "account.create.contactIndividual",
          <ContactIndividualFormItem
            activeModal={activeModal}
            setActiveModal={setActiveModal}
            formInstance={form}
            activeModalOptions={activeModalOptions}
          />
        )}
        <Row justify="center" gutter={20}>
          <Col>
            <Form.Item>
              <Link to="/accounts/list">
                <Button>Отмена</Button>
              </Link>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isCreateInProcess}>
                Создать
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {/* <Button
        onClick={() => {
          console.log(form.getFieldsValue());
        }}
      >
        values
      </Button> */}
    </div>
  );
}

export default AccountCreate;
